import { DateInput, NumberInput } from '@frontend/components/basic-forms';
import { EnumSelectListV2, ObjectSelectMapper } from '@frontend/components/select';
import { changeFilter } from '@frontend/repository';
import { format } from 'date-fns';
import React from 'react';
import { createPortal } from 'react-dom';
import { LuChevronLeft, LuCircleHelp } from 'react-icons/lu';

import { CommonMessage } from '@frontend/lang';
import { FilterProps } from '../filter.component';
import useFilterModal from './filter-modal.controller';

const ID = 'filter-modal';
export interface FilterModalProps<T extends object & { id: string }> extends FilterProps<T> {
    handleClose: () => void;
}

const FilterModal = <T extends object & { id: string }>(props: FilterModalProps<T>) => {
    const viewProps = useFilterModal<T>(props);

    const root = document.getElementById('root');
    if (!root) throw new Error('Root node not found. Cannot render modal.');

    return createPortal(
        <div
            id={ID}
            tabIndex={-1}
            aria-hidden='false'
            className='bg-background-default fixed top-0 right-0 left-0 flex h-dvh w-full flex-col overflow-x-hidden overflow-y-auto'>
            <div className='border-border-default flex w-full items-center justify-between gap-4 self-stretch border-b bg-white px-5 py-3'>
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                        props.handleClose();
                    }}
                    className='size-10 content-center justify-items-center'
                    data-testid='navigation-header-back-button'>
                    <LuChevronLeft className='size-6' />
                </div>
                <div className='text-center text-xl font-semibold text-stone-950'>Filters</div>
                <div className='text-primary size-10 content-center justify-items-center'>
                    <LuCircleHelp className='size-6' />
                </div>
            </div>
            <div className='flex h-full flex-col items-start justify-start gap-4 p-4'>
                {viewProps.params &&
                    props.filter?.keys?.map((key) => {
                        if (typeof key === 'string') {
                            const extraProps = props.filter?.keyProps?.get(key) ?? {};
                            if ((key as string).includes('_id')) {
                                return (
                                    <ObjectSelectMapper<T>
                                        label={props.filter?.displayMap?.get(key) || undefined}
                                        key={key}
                                        id={key}
                                        objectKey={key as keyof T}
                                        submitted={false}
                                        useConditionedStyling={false}
                                        onChange={(newValue) => {
                                            props.dispatch(
                                                changeFilter({
                                                    listId: props.listId,
                                                    key: props.filter?.queryParamKeyOverwrite?.get(key.toString()) ?? key,
                                                    value: newValue
                                                })
                                            );
                                        }}
                                        value={viewProps.params?.[props.filter?.queryParamKeyOverwrite?.get(key.toString()) ?? key]}
                                        className='w-full'
                                        isClearable
                                        allowURLChange
                                        {...extraProps}
                                        {...props}
                                    />
                                );
                            }
                            if (['from', 'to', 'timestamp'].includes(key) || key.includes('timestamp')) {
                                let keyValue = key;
                                if (key.includes('from')) keyValue = 'after';
                                if (key.includes('to')) keyValue = 'before';
                                return (
                                    <DateInput
                                        key={key}
                                        id={key}
                                        label={
                                            props.filter?.displayMap?.get(key) ||
                                            key.replace(/_/g, ' ').charAt(0).toUpperCase() + key.replace(/_/g, ' ').slice(1)
                                        }
                                        useConditionedStyling={false}
                                        onChange={(newValue) => {
                                            props.dispatch(
                                                changeFilter({
                                                    listId: props.listId,
                                                    key: props.filter?.queryParamKeyOverwrite?.get(keyValue.toString()) ?? keyValue,
                                                    value: newValue ? format(newValue, "yyyy-MM-dd'T'HH:mm:ss.SSSSSSXXX") : undefined
                                                })
                                            );
                                        }}
                                        value={viewProps.params?.[props.filter?.queryParamKeyOverwrite?.get(keyValue.toString()) ?? keyValue]}
                                        submitted={false}
                                        className='w-full'
                                        isClearable
                                        {...extraProps}
                                        {...props}
                                    />
                                );
                            }
                            if (['row', 'column'].includes(key)) {
                                return (
                                    <NumberInput
                                        key={key}
                                        id={key}
                                        label={props.filter?.displayMap?.get(key) || key}
                                        onChange={(value) => {
                                            props.dispatch(
                                                changeFilter({
                                                    listId: props.listId,
                                                    key: props.filter?.queryParamKeyOverwrite?.get(key.toString()) ?? key,
                                                    value: value.toString()
                                                })
                                            );
                                        }}
                                        value={viewProps.params?.[props.filter?.queryParamKeyOverwrite?.get(key.toString()) ?? key]}
                                        className='w-full'
                                        isClearable
                                        allowURLChange
                                        {...extraProps}
                                        {...props}
                                    />
                                );
                            }
                        }
                        if (key in EnumSelectListV2) {
                            const EnumInput = EnumSelectListV2[key as keyof typeof EnumSelectListV2];
                            const extraProps = props.filter?.keyProps?.get(key as string) ?? {};

                            return (
                                <EnumInput
                                    key={key}
                                    id={key}
                                    label={(typeof key === 'string' && props.filter?.displayMap?.get(key)) || undefined}
                                    isClearable
                                    value={viewProps.params?.[props.filter?.queryParamKeyOverwrite?.get(key.toString()) ?? key] as any}
                                    required={false}
                                    onSelect={(newValue) => {
                                        props.dispatch(
                                            changeFilter({
                                                listId: props.listId,
                                                key: props.filter?.queryParamKeyOverwrite?.get(key.toString()) ?? key,
                                                value: newValue?.value
                                            })
                                        );
                                    }}
                                    className='w-full'
                                    {...extraProps}
                                />
                            );
                        }
                        return <></>;
                    })}
            </div>

            <div
                className='flex w-full justify-center px-5 pb-4'
                onClick={(e) => {
                    e.stopPropagation();
                    props.handleClose();
                }}>
                <div
                    data-variant='Primary'
                    className='bg-brand-primary sticky inline-flex min-w-48 items-center justify-center gap-2 rounded-xl px-5 py-4'>
                    <div className='justify-start text-base font-medium text-white'>{CommonMessage.BUTTONS.SHOW_RESULTS}</div>
                </div>
            </div>
        </div>,
        root
    );
};

export default FilterModal;
